// Keep the native select as the source of truth for Formie and integrations.
export default class SelectDropdown {
    constructor(field) {
        this.field = field;
        this.select = field.querySelector('select:not([multiple])');
        this.wrapper = this.select?.closest('.form-floating');
        const label = field.querySelector('[data-formie-field-label]');
        if (!this.select || !this.wrapper || !label) return;

        const id = this.select.id + '-dropdown';
        label.id = id + '-label';
        label.classList.add('checkbox-dropdown-legend');
        this.select.classList.add('styled-select-native');
        this.select.tabIndex = -1;
        this.select.setAttribute('aria-hidden', 'true');
        this.wrapper.classList.add('checkbox-dropdown');

        this.trigger = document.createElement('button');
        this.trigger.type = 'button';
        this.trigger.className = 'input checkbox-dropdown-trigger';
        this.trigger.setAttribute('aria-haspopup', 'listbox');
        this.trigger.setAttribute('aria-expanded', 'false');
        this.trigger.setAttribute('aria-controls', id + '-options');
        this.trigger.setAttribute('aria-labelledby', label.id + ' ' + id + '-value');
        const floatingLabel = document.createElement('span');
        floatingLabel.className = 'checkbox-dropdown-label';
        floatingLabel.setAttribute('aria-hidden', 'true');
        floatingLabel.textContent = label.textContent.trim();
        this.summary = document.createElement('span');
        this.summary.id = id + '-value';
        this.summary.className = 'checkbox-dropdown-value';
        this.trigger.append(floatingLabel, this.summary);

        this.panel = document.createElement('div');
        this.panel.id = id + '-options';
        this.panel.className = 'checkbox-dropdown-panel select-dropdown-panel is-closed';
        this.panel.setAttribute('role', 'listbox');
        this.panel.setAttribute('aria-labelledby', label.id);
        this.panel.inert = true;
        this.panel.setAttribute('aria-hidden', 'true');
        this.options = [...this.select.options].map((option, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'select-dropdown-option';
            button.setAttribute('role', 'option');
            button.tabIndex = -1;
            button.textContent = option.text;
            button.disabled = option.disabled || option.parentElement.disabled;
            button.hidden = option.hidden;
            button.addEventListener('click', () => {
                this.select.selectedIndex = index;
                this.select.dispatchEvent(new Event('input', { bubbles: true }));
                this.select.dispatchEvent(new Event('change', { bubbles: true }));
                this.update();
                this.setOpen(false);
                this.trigger.focus();
            });
            this.panel.append(button);
            return button;
        });
        this.wrapper.append(this.trigger, this.panel);
        this.update();

        this.trigger.addEventListener('click', () => {
            this.setOpen(!this.isOpen);
            if (this.isOpen) this.focusSelected();
        });
        this.wrapper.addEventListener('keydown', (event) => {
            const options = this.options.filter((option) => !option.disabled && !option.hidden);
            const current = options.indexOf(document.activeElement);
            if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                event.preventDefault();
                this.setOpen(true);
                if (current === -1 && event.key.startsWith('Arrow')) {
                    this.focusSelected();
                } else {
                    const index = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
                        : (current + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
                    options[index]?.focus();
                }
            } else if (event.key === 'Escape' && this.isOpen) {
                event.preventDefault();
                event.stopPropagation();
                this.setOpen(false);
                this.trigger.focus();
            } else if (event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                event.preventDefault();
                this.search = (this.search || '') + event.key.toLowerCase();
                clearTimeout(this.searchTimeout);
                this.searchTimeout = setTimeout(() => { this.search = ''; }, 500);
                this.setOpen(true);
                options.find((option) => option.textContent.toLowerCase().startsWith(this.search))?.focus();
            }
        });
        this.wrapper.addEventListener('focusout', (event) => {
            if (!this.wrapper.contains(event.relatedTarget)) this.setOpen(false);
        });
        document.addEventListener('click', (event) => {
            if (!this.wrapper.contains(event.target)) this.setOpen(false);
        });
        this.select.addEventListener('change', () => this.update());
        this.select.addEventListener('focus', () => this.trigger.focus());
        this.select.addEventListener('invalid', (event) => {
            event.preventDefault();
            this.setOpen(true);
            this.trigger.focus();
        });
        const form = this.select.form;
        form.addEventListener('formie:validator:show-error', (event) => {
            if (event.target === this.select) {
                // Let Formie focus the first invalid field without leaving it inert.
                this.setOpen(true);
                this.syncValidation();
            }
        });
        form.addEventListener('formie:validator:clear-error', (event) => {
            if (event.target === this.select) this.syncValidation();
        });
        form.addEventListener('formie:submit:result', () => this.syncValidation());
        form.addEventListener('reset', () => {
            this.setOpen(false);
            this.trigger.removeAttribute('aria-invalid');
            setTimeout(() => this.update(), 0);
        });
    }

    update() {
        this.syncValidation();
        this.summary.textContent = this.select.selectedOptions[0]?.text || 'Select an option';
        this.summary.classList.toggle('is-placeholder', !this.select.value);
        this.trigger.disabled = this.select.disabled;
        this.options.forEach((option, index) => {
            option.setAttribute('aria-selected', String(index === this.select.selectedIndex));
        });
    }

    focusSelected() {
        const selected = this.options[this.select.selectedIndex];
        (selected && !selected.disabled && !selected.hidden ? selected
            : this.options.find((option) => !option.disabled && !option.hidden))?.focus();
    }

    syncValidation() {
        const invalid = this.field.matches('[data-formie-field-has-error="true"]')
            || !!this.field.querySelector('[aria-invalid="true"]:not(.checkbox-dropdown-trigger)');
        if (invalid) this.trigger.setAttribute('aria-invalid', 'true');
        else this.trigger.removeAttribute('aria-invalid');
        const errors = this.field.querySelector('[data-formie-field-errors]');
        if (invalid && errors?.id) this.trigger.setAttribute('aria-describedby', errors.id);
        else this.trigger.removeAttribute('aria-describedby');
    }

    setOpen(open) {
        this.panel.classList.toggle('is-closed', !open);
        this.panel.inert = !open;
        this.panel.setAttribute('aria-hidden', String(!open));
        this.trigger.setAttribute('aria-expanded', String(open));
    }

    get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
    }
}
